/**
 * Decision 0167: subscriptions by account. A subscription is an account (owner, provider, `quotaAccountKey`); the
 * Platform Runner subscription and the logins on paired machines that carry it are its connections. Pure.
 *
 * - §1 an account key groups connections only within one owner and one provider; a connection without a key is its own
 *   one-connection subscription. Nothing is merged by guessing.
 * - §2 limits: each quota window from the freshest observation across the account's connections.
 * - §4 sharing is set once per account; the first read takes the narrowest of what its connections had.
 * - §6 a person's choice per project names an account (`acct:<ref>`), which covers every connection of it.
 */
import type { ProfileScope } from "@minga/protocol"
import { sha256Hex } from "./hash"

/** The prefix of an account's key in a person's choice per project (§6). */
export const ACCOUNT_CHOICE_PREFIX = "acct:"

const refs = new Map<string, string>()

/**
 * Decision 0167 §6: an account's key in a person's choice per project and in the overview: `acct:` and a hash of the
 * owner and the account key. Opaque, so the raw account key never reaches a browser, and bound to the owner, so two
 * people's connections never share one (§1).
 */
export const accountChoiceKey = (ownerId: string, accountKey: string): string => {
  const material = `${ownerId}\n${accountKey}`
  let ref = refs.get(material)
  if (ref === undefined) {
    ref = sha256Hex(material).slice(0, 32)
    if (refs.size > 2000) refs.clear()
    refs.set(material, ref)
  }
  return `${ACCOUNT_CHOICE_PREFIX}${ref}`
}

export const isAccountChoiceKey = (key: string): boolean => key.startsWith(ACCOUNT_CHOICE_PREFIX)

/** What grouping reads of one connection (a Platform Runner subscription or a login on a machine). */
export interface AccountConnectionFacts {
  /** The connection's own key (core `subscriptionKey`: `<runnerId>:<id>`). */
  readonly key: string
  readonly ownerUserId: string
  readonly agent: string
  /** The opaque `quotaAccountKey` the connection's probe reported; null or absent = unknown. */
  readonly accountKey?: string | null | undefined
}

/** One account and its connections (§1). */
export interface AccountGroup<C extends AccountConnectionFacts> {
  /** `acct:<ref>` for an account with a key; the connection's own key for one without. */
  readonly key: string
  readonly ownerUserId: string
  readonly agent: string
  readonly accountKey: string | null
  readonly connections: ReadonlyArray<C>
}

/**
 * Decision 0167 §1: connections grouped by account. Same owner, same provider and same account key group; anything else
 * stays apart (two people with the same key, a connection without a key). Groups keep the connections' first order.
 */
export const groupAccounts = <C extends AccountConnectionFacts>(
  connections: ReadonlyArray<C>,
): ReadonlyArray<AccountGroup<C>> => {
  const groups = new Map<string, { group: Omit<AccountGroup<C>, "connections">; connections: C[] }>()
  for (const c of connections) {
    const accountKey = c.accountKey ? c.accountKey : null
    const key = accountKey ? accountChoiceKey(c.ownerUserId, accountKey) : c.key
    // the provider is part of the identity too: one key is never Claude and Codex at once
    const id = accountKey ? `${key}\n${c.agent}` : `conn\n${c.key}`
    const g = groups.get(id)
    if (g) g.connections.push(c)
    else
      groups.set(id, {
        group: { key, ownerUserId: c.ownerUserId, agent: c.agent, accountKey },
        connections: [c],
      })
  }
  return [...groups.values()].map((g) => ({ ...g.group, connections: g.connections }))
}

// #region sharing
/** Sharing as stored per connection and per account (0166 §3.1). */
export interface Sharing {
  readonly scope: ProfileScope
  readonly projectIds: ReadonlyArray<string>
}

const rank = (s: Sharing): number => (s.scope === "team" ? 2 : s.scope === "projects" ? 1 : 0)

/** A sharing in its canonical form: `projects` with no project is Personal; ids sorted and unique. */
export const canonicalSharing = (s: Sharing): Sharing =>
  s.scope === "projects"
    ? s.projectIds.length > 0
      ? { scope: "projects", projectIds: [...new Set(s.projectIds)].sort() }
      : { scope: "personal", projectIds: [] }
    : { scope: s.scope, projectIds: [] }

export const sameSharing = (a: Sharing, b: Sharing): boolean => {
  const x = canonicalSharing(a)
  const y = canonicalSharing(b)
  return x.scope === y.scope && x.projectIds.join("\n") === y.projectIds.join("\n")
}

/**
 * Decision 0167 §4: the narrowest of several connections' sharing, so nobody gets wider access by the merge: Personal <
 * some projects < Team, and some projects is the intersection of their projects (none in common is Personal).
 */
export const narrowestSharing = (all: ReadonlyArray<Sharing>): Sharing => {
  if (all.length === 0) return { scope: "personal", projectIds: [] }
  const canon = all.map(canonicalSharing)
  const low = Math.min(...canon.map(rank))
  if (low === 0) return { scope: "personal", projectIds: [] }
  if (low === 2) return { scope: "team", projectIds: [] }
  // some projects: those every `projects` connection lists (a Team one shares every project)
  const lists = canon.filter((s) => s.scope === "projects").map((s) => s.projectIds)
  const common = (lists[0] ?? []).filter((id) => lists.every((l) => l.includes(id)))
  return canonicalSharing({ scope: "projects", projectIds: common })
}
// #endregion sharing

// #region limits
/** One quota window as a connection's probe reported it (protocol `QuotaWindow`). */
export interface QuotaWindowFacts {
  readonly id?: string | undefined
  readonly model?: string | undefined
  readonly modelTag?: string | undefined
  readonly window: "5h" | "24h" | "7d" | "other"
  readonly usedPercent: number
  readonly resetsAt: number | null
}

/** A window with when it was observed. */
export type ObservedWindow<W extends QuotaWindowFacts = QuotaWindowFacts> = W & {
  readonly observedAt: number | null
}

/**
 * One limit across connections: its length and the model it is scoped to. Native ids differ by source for the same
 * window (a local probe's `session`/`weekly_all`/`weekly_scoped:fable`, the Platform's `five_hour`/`seven_day`/
 * `seven_day_opus`), so the id only names the scope when nothing else does.
 */
const windowScope = (w: QuotaWindowFacts): string =>
  w.modelTag ??
  w.model ??
  /scoped:(.+)$/.exec(w.id ?? "")?.[1] ??
  /^seven_day_(?!oauth)(.+)$/.exec(w.id ?? "")?.[1] ??
  ""
const windowIdentity = (w: QuotaWindowFacts): string =>
  w.window === "other" ? `other:${w.id ?? windowScope(w)}` : `${w.window}:${windowScope(w).toLowerCase()}`

const WINDOW_ORDER = { "5h": 0, "24h": 1, "7d": 2, other: 3 } as const

/**
 * Decision 0167 §2: an account's limits. Limits are account-wide, so each window (by its native id, else its length and
 * model) comes from the freshest observation among the connections that reported it. Account-wide windows first, short
 * before long.
 */
export const freshestLimits = <W extends QuotaWindowFacts>(
  observations: ReadonlyArray<{ readonly quota: ReadonlyArray<W>; readonly observedAt: number | null }>,
): ReadonlyArray<ObservedWindow<W>> => {
  const best = new Map<string, ObservedWindow<W>>()
  for (const o of observations)
    for (const w of o.quota) {
      const id = windowIdentity(w)
      const prior = best.get(id)
      if (!prior || (o.observedAt ?? -1) > (prior.observedAt ?? -1))
        best.set(id, { ...w, observedAt: o.observedAt })
    }
  return [...best.values()].sort(
    (a, b) =>
      Number(a.model !== undefined || a.modelTag !== undefined) -
        Number(b.model !== undefined || b.modelTag !== undefined) ||
      WINDOW_ORDER[a.window] - WINDOW_ORDER[b.window] ||
      windowIdentity(a).localeCompare(windowIdentity(b)),
  )
}
// #endregion limits

// #region choice
/**
 * Decision 0167 §6: a person's choice with each connection key read as its account: a `<runnerId>:<id>` key of a
 * connection that has an account adds that account's key (the connection key stays, so nothing narrows). Unknown keys
 * are kept as they are.
 */
export const choiceWithAccounts = (
  keys: ReadonlyArray<string>,
  connections: ReadonlyArray<{ readonly key: string; readonly accountChoiceKey: string | null }>,
): ReadonlyArray<string> => {
  const out = new Set(keys)
  for (const k of keys) {
    if (isAccountChoiceKey(k)) continue
    const account = connections.find((c) => c.key === k)?.accountChoiceKey
    if (account) out.add(account)
  }
  return [...out]
}
// #endregion choice
